/**
 * Arrow-key movement through a menu.
 *
 * Pure, so `npm test` can reach it — and worth reaching, because the list a menu walks is not the
 * list it renders: captions, separators and the `No other tabs open` note are rows on screen and
 * must be **skipped**, and a disabled item must be too. An off-by-one here reads as the menu
 * ignoring a keypress, which is exactly the kind of thing nobody reports and nobody notices until
 * they try to use the menu without a mouse.
 *
 * None of the three menus this replaces had keyboard navigation at all, so there is no behaviour
 * to preserve — only `role="menu"`'s contract to meet.
 */

import { isSelectable, type MenuItemSpec } from "./menuSpec";

export type MenuNavKey = "ArrowDown" | "ArrowUp" | "Home" | "End";

/**
 * The index this key moves focus to, or `null` when there is nowhere to go.
 *
 * Wraps at both ends, which is what a `role="menu"` is expected to do and what makes a short menu
 * navigable with one key. `null` for a menu with no selectable row at all — a `Move to log`
 * submenu whose only content is `No other tabs open` is exactly that, and returning an index would
 * focus a line you cannot choose.
 */
export function moveFocus(
  items: readonly MenuItemSpec[],
  current: number | null,
  key: MenuNavKey,
): number | null {
  const selectable = items
    .map((item, index) => (isSelectable(item) ? index : -1))
    .filter((index) => index >= 0);

  const first = selectable[0];
  const last = selectable[selectable.length - 1];
  // `first` doubles as the empty check: with `noUncheckedIndexedAccess` on, narrowing it here is
  // what lets everything below return a plain `number`.
  if (first === undefined || last === undefined) return null;

  if (key === "Home") return first;
  if (key === "End") return last;

  const step = key === "ArrowDown" ? 1 : -1;
  // Opening with ArrowUp lands on the last item, which is the convention and is genuinely useful
  // here: every menu in this app puts its destructive action at the bottom.
  const fromNowhere = step === 1 ? first : last;

  if (current === null) return fromNowhere;

  const position = selectable.indexOf(current);
  // Focus sitting on a row that is no longer selectable — a submenu list that shrank under it —
  // restarts from the end the key is travelling from rather than refusing to move.
  if (position === -1) return fromNowhere;

  return selectable[(position + step + selectable.length) % selectable.length] ?? fromNowhere;
}
